import React from 'react';
import {useSize} from '../../themes';
import {useFormGroupId} from '../FormGroup';
import {Text} from '../Text';
import {
  ElementWrapperView,
  SwitchElementView,
  SwitchKnobView,
  SwitchLabelView,
  SwitchTrackView,
  SwitchView,
} from './Switch.styles';
import {SwitchLabelProps, SwitchProps} from './_types';

const SwitchLabel = ({
  density,
  isChecked,
  label,
  shouldShowOnOffLabel,
}: SwitchLabelProps) => {
  if (!label && !shouldShowOnOffLabel) return null;

  const onOffLabel = isChecked ? 'On' : 'Off';
  const labelContent = label || onOffLabel;
  const isHidden = typeof labelContent !== 'string';

  return (
    <SwitchLabelView aria-hidden={isHidden} density={density}>
      <Text truncateToLines={1}>{labelContent}</Text>
    </SwitchLabelView>
  );
};

/**
 * # Switch
 *
 * Switches allow users to turn an option on or off.
 *
 * ## Usage
 *
 * ```jsx
 * import {Switch} from '@packages/systems/spring';
 *
 * function Example() {
 *   const [checked, setChecked] = React.useState(false);
 *
 *   return (
 *     <Switch
 *       isChecked={checked}
 *       label="Some Label"
 *       onChange={event => setChecked(event.target.checked)}
 *     />
 *   );
 * }
 * ```
 *
 * @component
 */
export const Switch = React.forwardRef<HTMLLabelElement, SwitchProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      defaultIsChecked,
      id: idProp,
      isChecked,
      isDisabled = false,
      label,
      onChange = () => undefined,
      shouldShowOnOffLabel = false,
      size: sizeProp,
      density = 'comfortable',
      ...rest
    }: SwitchProps,
    forwardedRef
  ) => {
    const id = useFormGroupId(idProp);
    const sizeContext = useSize();

    /**
     * We're doing this because Switch renders a size outside the range
     * of SizeContext.
     */
    const size = sizeProp || sizeContext;

    const elementRef = React.useRef<HTMLInputElement>(null);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(event);

      /**
       * Ensures that keyboard-triggered change events like `SPACE` and
       * `CTRL`+`OPT`+`SPACE` works as expected, specifically for voice-over mode.
       *
       * Since we also have the handleOnChange callback on the parent element,
       * stopping the event propagation enables the native (switch) change
       * event work correctly between the <SwitchElement /> (button/input) and the
       * wrapping <View />.
       */
      event.stopPropagation();

      if (elementRef.current && document.activeElement !== elementRef.current) {
        elementRef.current.focus();
      }
    };

    const elementProps = {
      'aria-checked': Boolean(isChecked),
      'aria-describedby': ariaDescribedBy,
      'aria-label': ariaLabel,
      checked: isChecked,
      defaultChecked: defaultIsChecked,
      disabled: isDisabled,
      id,
      onChange: handleOnChange,
      ref: elementRef,
      role: 'switch',
      type: 'checkbox',
    } as const;

    return (
      <SwitchView
        {...rest}
        htmlFor={id}
        isDisabled={isDisabled}
        ref={forwardedRef}
        size={size}
      >
        <ElementWrapperView hasNoLabel={!(label || shouldShowOnOffLabel)}>
          <SwitchElementView {...elementProps} />
          <SwitchTrackView
            density={density}
            isChecked={isChecked}
            isDisabled={isDisabled}
          >
            <SwitchKnobView density={density} isChecked={isChecked} />
          </SwitchTrackView>
        </ElementWrapperView>
        <SwitchLabel
          density={density}
          isChecked={isChecked}
          label={label}
          shouldShowOnOffLabel={shouldShowOnOffLabel}
        />
      </SwitchView>
    );
  }
);

Switch.displayName = 'Switch';
Switch.toString = SwitchView.toString;

export default Switch;
